import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import { useOutletContext } from 'react-router-dom';
import { List, Skeleton, Tooltip } from 'antd';
import Details from '@pages/MediaViewer/Details';
import { MediaViewerOutletContextState } from '@layouts/types';
import classNames from 'classnames';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { AssetViewerStateProvider } from '@context/AssetViewerStateContext';
import { getBrowser, getOS } from '@helpers/withKeysCanvasHistoryChange';

import { ReactComponent as CommentsSvg } from '@assets/icons/MediaViewer/comments.svg';
import { ReactComponent as TaskSvg } from '@assets/icons/MediaViewer/task.svg';
// import { ReactComponent as ActivitySvg } from '@assets/icons/MediaViewer/activity.svg';
import { ReactComponent as FileInfoSvg } from '@assets/icons/MediaViewer/file-info.svg';
import CompareVersionsModalContainer from '@pages/MediaViewer/components/CompareVersionsModal/CompareVersionsModalContainer';
import FloatCommentEditor from '@pages/MediaViewer/components/FloatCommentEditor';
import FloatCommentItem from '@pages/MediaViewer/components/FloatCommentItem';
import TranscriptDrawerContainer from '@pages/MediaViewer/components/TranscriptDrawer';

import './MediaViewer.less';

const FabricContextProvider = React.lazy(
  () => import('@context/FabricContext')
);
const AssetViewer = React.lazy(() => import('@pages/MediaViewer/AssetViewer'));
const Comments = React.lazy(() => import('@pages/MediaViewer/Comments'));
const Tasks = React.lazy(() => import('@pages/MediaViewer/Tasks'));
// const Activity = React.lazy(() => import('@pages/MediaViewer/Activity'));
const AssetsSlider = React.lazy(
  () => import('@pages/MediaViewer/components/AssetsSlider')
);

type MediaViewerProps = {
  commentMentions: boolean;
  isLoadingAsset?: boolean;
  isShowAssetsSlider?: boolean;
};

function getKeysMediaViewerShortcuts() {
  const macos = {
    comments: 'Shift + Option + 1',
    tasks: 'Shift + Option + 2',
    // activity: 'Shift + Option + 3',
    fileInfo: 'Shift + Option + 3'
  };
  const windows = {
    comments: 'Shift + Alt + 1',
    tasks: 'Shift + Alt + 2',
    // activity: 'Shift + Alt + 3',
    fileInfo: 'Shift + Alt + 3'
  };
  const os = getOS();
  const browser = getBrowser();
  if (os === 'macos') {
    if (browser === 'safari') return macos;
    if (browser === 'chrome') return macos;
  }
  if (os === 'windows') return windows;
  return windows;
}

function MediaViewer({
  commentMentions,
  isShowAssetsSlider = true,
  isLoadingAsset = false
}: MediaViewerProps) {
  const { commentsList, isLoadingCommentsList } =
    useOutletContext<MediaViewerOutletContextState>();

  const canViewTasks = useAssetMVPermissions('listTasks');
  const canCreateTask = useAssetMVPermissions('convertCommentToTask');

  const canReadComment = useAssetMVPermissions('readComments');
  const canLeaveComment = useAssetMVPermissions('comment');
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const [selectedCollapse, setSelectedCollapse] = useState<string | null>(null);

  const isShowFloatCommentForm =
    asset && canLeaveComment && !!selectedVersion && !selectedCollapse;

  const isShowFloatCommentItem =
    asset && !!selectedVersion && !selectedCollapse;

  const sidebarMenuItems = useMemo(() => {
    const keys = getKeysMediaViewerShortcuts();
    return [
      {
        key: 'comments',
        icon: <CommentsSvg />,
        tooltip: (
          <>
            Comments Panel
            <span className="media-viewer-tooltip-label">{keys.comments}</span>
          </>
        )
      },
      ...(canViewTasks
        ? [
            {
              key: 'tasks',
              icon: <TaskSvg />,
              className: 'tour-create-task-from-comment-tasks',
              tooltip: (
                <>
                  Tasks Panel
                  <span className="media-viewer-tooltip-label">
                    {keys.tasks}
                  </span>
                </>
              )
            }
          ]
        : []),
      // {
      //   key: 'activity',
      //   icon: <ActivitySvg />,
      //   tooltip: (
      //     <>
      //       Activity Panel
      //       <span className="media-viewer-tooltip-label">{keys.activity}</span>
      //     </>
      //   )
      // },
      {
        key: 'fileInfo',
        icon: <FileInfoSvg />,
        tooltip: (
          <>
            Details Panel
            <span className="media-viewer-tooltip-label">{keys.fileInfo}</span>
          </>
        )
      }
    ];
  }, [canViewTasks]);

  const onToggleOpenSelectedCollapse = useCallback(
    (value: string | null) => {
      setSelectedCollapse((prevSelectedCollapse) => {
        if (prevSelectedCollapse === value) return null;
        return value;
      });
    },
    [setSelectedCollapse]
  );

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      const isShortcutModifier = e.shiftKey && e.altKey;
      const keyCode = e.code;
      if (!isShortcutModifier) return;
      if (keyCode === 'Digit1') {
        onToggleOpenSelectedCollapse('comments');
        return;
      }
      if (keyCode === 'Digit2' && canViewTasks) {
        onToggleOpenSelectedCollapse('tasks');
        return;
      }
      if (keyCode === 'Digit3') {
        onToggleOpenSelectedCollapse('fileInfo');
      }
    };

    document.addEventListener('keydown', onKeyPress);
    return () => {
      document.removeEventListener('keydown', onKeyPress);
    };
  }, [canViewTasks, onToggleOpenSelectedCollapse]);

  const loader = (
    <div className="media_viewer_container">
      <div className="media_viewer_content">
        <PlaybackPreparingLoader />
      </div>
      <div className="media_viewer_sidebar" />
    </div>
  );

  if (!selectedVersion || isLoadingAsset) return loader;
  const viewerStateKey = `${asset?.id ?? ''}-${selectedVersion?.id ?? ''}`;
  return (
    <Suspense fallback={loader}>
      <div
        className={classNames({
          media_viewer_container: true,
          'b-content-full': !canReadComment && !canLeaveComment,
          'b-task-hide': !canViewTasks
        })}
      >
        <AssetViewerStateProvider
          key={viewerStateKey}
          selectedVersion={selectedVersion}
          commentsList={commentsList ?? []}
        >
          <FabricContextProvider>
            <div className="media_viewer_content">
              <TranscriptDrawerContainer />
              {isShowAssetsSlider && <AssetsSlider />}
              <AssetViewer setSelectedCollapse={setSelectedCollapse} />
              {isShowFloatCommentForm && (
                <FloatCommentEditor
                  asset={asset}
                  canCreateTask={canCreateTask}
                  commentMentions={commentMentions}
                  selectedVersion={selectedVersion}
                />
              )}
              {isShowFloatCommentItem && (
                <FloatCommentItem
                  asset={asset}
                  selectedVersion={selectedVersion}
                  commentMentions={commentMentions}
                  canCreateTask={canCreateTask}
                  openCommentsPanel={() =>
                    onToggleOpenSelectedCollapse('comments')
                  }
                />
              )}
            </div>
            {(canReadComment || canLeaveComment) && (
              <div className="media_viewer_sidebar">
                <div className="media_viewer_sidebar-menu">
                  {sidebarMenuItems.map((item) => (
                    <Tooltip
                      key={item.key}
                      title={
                        <div
                          className="media-viewer-tooltip-inner"
                          style={{ padding: 8 }}
                        >
                          {item.tooltip}
                        </div>
                      }
                      overlayInnerStyle={{ padding: 0 }}
                      placement="right"
                      overlayClassName="toggle_favorite_overlay"
                    >
                      <div className="media_viewer_sidebar-menu-item">
                        <div
                          onClick={() => onToggleOpenSelectedCollapse(item.key)}
                          className={classNames(
                            'media_viewer_sidebar-menu-btn',
                            item.className,
                            {
                              active: item.key === selectedCollapse
                            }
                          )}
                        >
                          {item.icon}
                        </div>
                      </div>
                    </Tooltip>
                  ))}
                </div>
                <div
                  className={classNames('media_viewer_sidebar-content', {
                    'b-open': !!selectedCollapse
                  })}
                >
                  <Suspense
                    fallback={
                      <List
                        className="media_viewer_list-skeleton"
                        itemLayout="horizontal"
                        style={{ padding: 16, width: '100%' }}
                        bordered={false}
                        split={false}
                        dataSource={new Array(10)
                          .fill(undefined)
                          .map((_, i) => ({ key: i }))}
                        renderItem={() => (
                          <List.Item>
                            <Skeleton.Input style={{ height: 88 }} active />
                          </List.Item>
                        )}
                      />
                    }
                  >
                    {selectedCollapse === 'comments' &&
                      !!selectedVersion &&
                      !!asset && (
                        <Comments
                          canCreateTask={canCreateTask}
                          selectedVersion={selectedVersion}
                          commentsList={commentsList}
                          isLoadingCommentsList={isLoadingCommentsList}
                          asset={asset}
                          onClosePanel={() =>
                            onToggleOpenSelectedCollapse(null)
                          }
                          commentMentions={commentMentions}
                        />
                      )}

                    {selectedCollapse === 'tasks' &&
                      canViewTasks &&
                      !!selectedVersion &&
                      !!asset && (
                        <Tasks
                          onToggleOpenSelectedCollapse={
                            onToggleOpenSelectedCollapse
                          }
                          canCreateTask={canCreateTask}
                          selectedVersion={selectedVersion}
                          asset={asset}
                        />
                      )}
                    {/* {selectedCollapse === 'activity' && (
                      <Activity
                        onClosePanel={() => onToggleOpenSelectedCollapse(null)}
                      />
                    )} */}
                    {selectedCollapse === 'fileInfo' && (
                      <Details
                        onClosePanel={() => onToggleOpenSelectedCollapse(null)}
                      />
                    )}
                  </Suspense>
                </div>
              </div>
            )}
          </FabricContextProvider>
        </AssetViewerStateProvider>
        <CompareVersionsModalContainer />
      </div>
    </Suspense>
  );
}

export default MediaViewer;
